import { useEffect, useRef, useState } from "react";
import type { StationStatus } from "@ees/shared";
import { api, connectStation, describeLive } from "./api.js";
import { attachStream } from "./useStream.js";
import { Meters, NowPlayingCard, formatClock } from "./components/Player.js";
import { Console } from "./components/Console.js";
import { Landing } from "./Landing.js";
import { ModePicker, Visualizer, useVisualizerMode } from "./visualizer/Visualizer.js";
import type { Backend } from "./visualizer/renderer.js";

const OFFLINE: StationStatus = {
  mode: "offline",
  stationName: "Essential EDM Sessions",
  nowPlaying: null,
  upcoming: [],
  live: null,
  master: { peakDb: [-90, -90], rmsDb: [-90, -90], limited: false },
  decks: [
    { id: "A", active: true, mediaId: null, title: null, artist: null, progress: 0, gainDb: 0, gain: 1, bpm: null, cued: false },
    { id: "B", active: false, mediaId: null, title: null, artist: null, progress: 0, gainDb: 0, gain: 1, bpm: null, cued: false },
  ],
  hls: { state: "starting", message: "connecting", hlsUrl: null, audioPlaylistUrl: null, icecastUrl: null, bitrateKbps: 0 },
  icecast: { state: "stopped", message: null, hlsUrl: null, audioPlaylistUrl: null, icecastUrl: null, bitrateKbps: 0 },
  listeners: 0,
  serverTime: 0,
};

function Stage({ status }: { status: StationStatus }): JSX.Element {
  const video = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const [vizMode, setVizMode] = useVisualizerMode();
  const [backend, setBackend] = useState<Backend | null>(null);
  const streamUrl = status.hls.hlsUrl ?? "/hls/master.m3u8";
  const item = status.nowPlaying;

  useEffect(() => {
    if (!video.current) return;
    // Re-attach whenever the playlist URL changes, but not on every status
    // frame: reloading hls.js mid-show would drop the buffer.
    return attachStream(video.current, streamUrl);
  }, [streamUrl]);

  const showPoster = failed || !item?.videoAvailable;

  return (
    <div className="stage">
      <video
        ref={video}
        autoPlay
        muted
        playsInline
        controls
        onError={() => setFailed(true)}
        aria-label="Essential EDM Sessions live stream"
      />
      {showPoster ? (
        <div className="stage__poster">
          <Visualizer
            getMedia={() => video.current}
            mode={vizMode}
            onBackend={setBackend}
          />
          {item ? (
            <>
              <span className="stage__title">{item.title}</span>
              {item.artist ? <span className="stage__artist">{item.artist}</span> : null}
            </>
          ) : (
            <span className="stage__idle">Station offline</span>
          )}
          <div className="bars" aria-hidden="true">
            {Array.from({ length: 28 }, (_, index) => (
              <span key={index} style={{ animationDelay: `${(index % 7) * 0.11}s` }} />
            ))}
          </div>
          <div className="viz-controls">
            <ModePicker mode={vizMode} onChange={setVizMode} compact />
          </div>
          {backend ? <span className="viz-badge">{backend}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function Schedule({ status }: { status: StationStatus }): JSX.Element {
  return (
    <section className="card" aria-label="Schedule">
      <div className="card__head">
        <h2 className="card__title">Up next</h2>
      </div>
      <div className="card__body">
        {status.upcoming.length === 0 ? (
          <p className="empty">Nothing scheduled — the station is running its library rotation.</p>
        ) : (
          <ul className="list">
            {status.upcoming.map((entry) => (
              <li className="list__row" key={`${entry.slotId}-${entry.startsAt}`}>
                <span className="list__time">{formatClock((entry.startsAt - status.serverTime) / 1000)}</span>
                <span className="list__name">{entry.title}</span>
                <span className="list__tag">{entry.kind}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function StationView(): JSX.Element {
  const [status, setStatus] = useState<StationStatus>(OFFLINE);

  useEffect(() => {
    // Seed immediately so the page is never blank, then let the socket take over.
    void api.status().then(setStatus).catch(() => undefined);
    return connectStation((message) => {
      if (message.type === "status") setStatus(message.data);
    });
  }, []);

  const modeClass =
    status.mode === "live" ? "pill pill--live" : status.mode === "automation" ? "pill pill--automation" : "pill pill--offline";

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar__inner">
          <div className="brand">
            <span className="brand__mark">{status.stationName}</span>
            <span className="brand__sub">Essential EDM Sessions</span>
          </div>
          <span className={modeClass}>
            <span className="dot" />
            {status.mode === "live" ? "Live" : status.mode === "automation" ? "Auto DJ" : "Offline"}
          </span>
          {status.live ? <span className="pill pill--live">{describeLive(status.live)}</span> : null}
        </div>
      </header>

      <main className="shell">
        <div className="grid grid--stage">
          <div className="stack">
            <Stage status={status} />
            <section className="card">
              <div className="card__body">
                <NowPlayingCard item={status.nowPlaying} />
              </div>
            </section>
          </div>

          <div className="stack">
            <section className="card" aria-label="Meters">
              <div className="card__head">
                <h2 className="card__title">Master</h2>
                {status.master.limited ? <span className="pill pill--live">Limiting</span> : null}
              </div>
              <div className="card__body">
                <Meters meters={status.master} />
                <div style={{ marginTop: "1rem" }}>
                  <span className="now__label">Decks</span>
                  <ul className="list" style={{ marginTop: "0.4rem" }}>
                    {status.decks.map((deck) => (
                      <li className="list__row" key={deck.id}>
                        <span className="list__time">DECK {deck.id}</span>
                        <span className="list__name">{deck.title ?? (deck.cued ? "cued" : "empty")}</span>
                        <span className="list__tag">{deck.active ? "on air" : "standby"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            <Schedule status={status} />
          </div>
        </div>

        <div style={{ marginTop: "1.1rem" }}>
          <Console />
        </div>
      </main>
    </div>
  );
}

export function App(): JSX.Element {
  // The front page is the landing view; the station player + console live behind
  // `#console` so the copied design stays intact.
  const [hash, setHash] = useState(() => window.location.hash);

  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  return hash === "#console" ? <StationView /> : <Landing />;
}
